import { ComponentType, createContext, FC, useContext } from 'react';
import { useSelector } from 'react-redux';
import identity from 'lodash/identity';
import { BOOKING_LIST_LOAD_LIMIT } from 'config/constants';
import { useBookingListQuery } from 'lib/graphql/generated';
import { getSsoUserMemberId } from 'store/authentication/sso/selectors';
import { BookingListContextValue } from './types';
import { createFetchMoreHandler, getBookingCountLabel, getCurrentFilterLabel, getQueryFilter } from './helpers';
import { DEFAULT_FILTER, OPTIONS, VALID_FILTERS } from './constants';
import { useFilter, usePreloadBookings } from '../hooks';
import sendGAEvent from 'utils/sendGAEvent';
import { GA } from 'types';
import sumBy from 'lodash/sumBy';
import capitalize from 'lodash/capitalize';

const BookingListContext = createContext<BookingListContextValue>({
  options: OPTIONS,
  filter: DEFAULT_FILTER,
  update: identity,
  loading: false,
  bookingListLabel: getCurrentFilterLabel(DEFAULT_FILTER),
  bookingCountLabel: getBookingCountLabel(),
  bookings: [],
  hasMore: false,
  loadMore: identity,
  error: null,
  sendViewBookingEvent: () => {},
});

const BookingListContextProvider: FC = ({ children }) => {
  const { filter, update } = useFilter({ validFilters: VALID_FILTERS, defaultFilter: DEFAULT_FILTER });
  const accountId = useSelector(getSsoUserMemberId);
  const { data, error, loading, fetchMore } = useBookingListQuery({
    variables: { accountId, limit: BOOKING_LIST_LOAD_LIMIT, offset: 0, filter: getQueryFilter(filter) },
    fetchPolicy: 'cache-and-network',
  });
  const bookings = data?.bookingList.bookings || [];
  const loadMore = data ? createFetchMoreHandler({ data, fetchMore, filter }) : identity;
  usePreloadBookings(accountId);

  const sendViewBookingEvent = (id: string) => {
    const index = bookings.findIndex((booking) => booking.id === id);

    if (index < 0) {
      return;
    }

    const {
      reservation: { property, roomType },
      nights,
      payableTotals,
    } = bookings[index];

    sendGAEvent({
      type: GA.EventType.SELECT_ITEM,
      itemListId: `${filter}_bookings`,
      itemListName: `${capitalize(filter)} Bookings`,
      currency: payableTotals[0].currency,
      items: [
        {
          itemId: id,
          itemName: property.name,
          quantity: nights,
          itemVariant: roomType.name,
          itemCategory: 'Hotel',
          index,
          price: sumBy(payableTotals, 'amount'),
        },
      ],
    });
  };

  const contextValue = {
    options: OPTIONS,
    filter,
    update,
    loading: !filter || loading,
    bookingListLabel: getCurrentFilterLabel(filter),
    bookingCountLabel: getBookingCountLabel(data),
    bookings,
    hasMore: !!data?.bookingList.hasMore,
    loadMore,
    error,
    sendViewBookingEvent,
  };

  return <BookingListContext.Provider value={contextValue}>{children}</BookingListContext.Provider>;
};

// eslint-disable-next-line react/display-name
export const withBookingListContext = (Component: ComponentType) => () =>
  (
    <BookingListContextProvider>
      <Component />
    </BookingListContextProvider>
  );

export const useBookingListContext = () => useContext(BookingListContext);
